import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Users,
  Shield,
  UserPlus,
  Mail,
  Trash2,
  Crown,
  User,
} from "lucide-react";
import { cn, formatDateTime } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-audit-tool/artifacts/aj4ztq87_image.png";

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate("/");
      return;
    }
    fetchData();
  }, [isSuperAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [usersRes, invitesRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/invited`),
      ]);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    try {
      await axios.post(
        `${API}/admin/invite`,
        { email: inviteEmail.trim() }
      );
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleRevokeInvite = async (email) => {
    try {
      await axios.delete(`${API}/admin/invite/${email}`);
      toast.success("Invitation revoked");
      fetchData();
    } catch (error) {
      toast.error("Failed to revoke invitation");
    }
  };

  const handleToggleRole = async (userId, currentRole) => {
    const newRole = currentRole === "super_admin" ? "brand_manager" : "super_admin";
    
    try {
      await axios.put(
        `${API}/admin/users/${userId}/role`,
        { role: newRole }
      );
      toast.success(`Role updated to ${newRole}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
    setConfirmAction(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  const superAdmins = users.filter((u) => u.role === "super_admin");
  const brandManagers = users.filter((u) => u.role === "brand_manager");

  return (
    <div className="min-h-screen bg-zinc-950 bg-grid">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-zinc-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img src={LOGO_URL} alt="Digital Impressions" className="h-8 w-auto" />
              <div>
                <h1 className="heading-sm text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  Admin Panel
                </h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Invite Admin */}
          <Card className="card-glass lg:col-span-1">
            <CardHeader>
              <CardTitle className="heading-md text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                Invite Super Admin
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInvite} className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="input-dark"
                    data-testid="invite-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={inviting || !inviteEmail.trim()}
                  className="w-full btn-primary"
                  data-testid="send-invite-btn"
                >
                  {inviting ? (
                    <div className="spinner w-4 h-4" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </form>

              {/* Pending Invites */}
              {invites.length > 0 && (
                <div className="mt-6 pt-6 border-t border-zinc-800">
                  <h3 className="text-sm font-medium text-zinc-400 mb-3">Pending Invitations</h3>
                  <div className="space-y-2">
                    {invites.map((invite) => (
                      <div
                        key={invite.email}
                        className="flex items-center justify-between p-2 bg-zinc-900/50 rounded-lg"
                      >
                        <span className="text-sm text-zinc-300 truncate">{invite.email}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRevokeInvite(invite.email)}
                          className="text-zinc-500 hover:text-red-400"
                          data-testid={`revoke-${invite.email}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Users List */}
          <Card className="card-glass lg:col-span-2">
            <CardHeader>
              <CardTitle className="heading-md text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                All Users ({users.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Super Admins */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  Super Admins ({superAdmins.length})
                </h3>
                <div className="space-y-2">
                  {superAdmins.map((user) => (
                    <div
                      key={user.user_id}
                      className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img
                            src={user.picture}
                            alt={user.name}
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-sm text-zinc-500">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-primary/20 text-primary border-primary/30">
                          Super Admin
                        </Badge>
                        {user.user_id !== currentUser?.user_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmAction({ userId: user.user_id, role: user.role, name: user.name })}
                            className="text-zinc-400 hover:text-white"
                          >
                            Demote
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Brand Managers */}
              <div>
                <h3 className="text-sm font-semibold text-zinc-400 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Brand Managers ({brandManagers.length})
                </h3>
                {brandManagers.length === 0 ? (
                  <p className="text-zinc-600 text-sm">No brand managers yet</p>
                ) : (
                  <div className="space-y-2">
                    {brandManagers.map((user) => (
                      <div
                        key={user.user_id}
                        className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img
                              src={user.picture}
                              alt={user.name}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                              <User className="w-5 h-5 text-zinc-500" />
                            </div>
                          )}
                          <div>
                            <p className="text-white font-medium">{user.name}</p>
                            <p className="text-sm text-zinc-500">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                            Brand Manager
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmAction({ userId: user.user_id, role: user.role, name: user.name })}
                            className="text-zinc-400 hover:text-primary"
                          >
                            Promote
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              {confirmAction?.role === "super_admin" ? "Demote to Brand Manager?" : "Promote to Super Admin?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirmAction?.role === "super_admin"
                ? `${confirmAction?.name} will lose admin privileges and only be able to see their own audits.`
                : `${confirmAction?.name} will gain admin privileges and be able to see all audits and manage users.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmAction && handleToggleRole(confirmAction.userId, confirmAction.role)}
              className={confirmAction?.role === "super_admin" ? "bg-red-600 hover:bg-red-700" : "btn-primary"}
            >
              {confirmAction?.role === "super_admin" ? "Demote" : "Promote"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
